"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  LayoutDashboard,
  Receipt,
  Kanban,
  Bot,
  Cable,
  Settings,
  Shield,
  ShieldOff,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChartColumn,
  ListChecks,
  BarChart3,
  FileBarChart,
  Map,
  Users,
  Funnel,
  GitBranch,
  Repeat2,
  Columns3,
  Headset,
  Inbox,
  Sliders,
  // Brain, // usado só pelo GK.IA (oculto)
  Wallet,
  Heart,
  ClipboardList,
  Siren,
  Moon,
  Sun,
  LogOut,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Gauge,
  Workflow,
  Zap,
  Trophy,
  Monitor,
  MessageCircle,
  MessageSquareReply,
  MessagesSquare,
  Hash,
  Activity,
  UserCog,
  HeartHandshake,
  Route,
  Star,
  UserX,
  Megaphone,
  Contact,
  PauseCircle,
  Tags,
  CalendarDays,
  Building2,
  Lock,
  Phone,
  HandCoins,
  CalendarClock,
  ArrowLeftRight,
  Wifi,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { findActiveGroupKey } from "@/components/layout/sidebar-active-group";
import { isModuleItemVisible } from "@/components/layout/sidebar-visibility";
import { hasPermission, getIsGkAdmin, hasAnyPermission, hasModule, getVoiceEnabled, getAgendaEnabled } from "@/lib/permissions";
import { useLivePermissions } from "@/hooks/use-live-permissions";
import { useInternalUnread } from "@/hooks/internal-unread-context";
import { unreadTier } from "@/lib/chat/unread-tier";
import { playNotificationSoundIfEnabled } from "@/lib/notification-sounds";
import { NotificationBell } from "@/components/layout/notification-bell";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarAgentStatus } from "@/components/layout/sidebar-agent-status";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useRef, useState } from "react";
import { logout } from "@/lib/auth";
import { withSpacesTransition } from "@/lib/transitions";
import type { AuthUser } from "@/lib/api";
import { useFotoQueFalha } from '@/hooks/use-foto-que-falha';
import { iniciais } from '@/lib/iniciais';
import { unreadAnchor } from "./sidebar-unread-anchor";

interface SubItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  /** Visível se QUALQUER uma for concedida (OR, com herança por climb). */
  anyOfPermissions?: string[];
  anyPermission?: string;
  badge?: string;
  disabled?: boolean;
  gkAdminOnly?: boolean;
  /** Gate do controle de módulos por ISP (hasModule) — ex.: "SUPORTE". */
  module?: string;
}

// Sub-grupo colapsável de 2º nível dentro de um módulo (ex.: Chat ->
// Operação / Relatórios / Configurações). Reusa SubItem sem alteração.
interface SubGroup {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  items: SubItem[];
}

interface ModuleItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  alwaysVisible?: boolean;
  module?: string;
  permission?: string;
  /** Visível se QUALQUER uma for concedida (OR) — usado pelo Chat (chat OU suporte). */
  anyOfPermissions?: string[];
  children?: SubItem[];
  groups?: SubGroup[];
  comingSoon?: boolean;
  badge?: string;
}

const modules: ModuleItem[] = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    alwaysVisible: true,
  },
  {
    name: "Chat",
    href: "/dashboard/chat",
    icon: MessagesSquare,
    anyOfPermissions: ["chat.conversations.view", "suporte.view"],
    groups: [
      {
        name: "Operação",
        icon: Headset,
        items: [
          {
            name: "Conversas",
            href: "/dashboard/chat",
            icon: MessageCircle,
            permission: "chat.conversations.view",
          },
          {
            name: "Agenda",
            href: "/dashboard/agenda",
            icon: CalendarClock,
            permission: "agenda.callbacks.ver",
          },
          {
            name: "Contatos",
            href: "/dashboard/chat/contatos",
            icon: Contact,
            permission: "chat.contacts.view",
          },
          {
            name: "Campanhas",
            href: "/dashboard/chat/campanhas",
            icon: Megaphone,
            permission: "chat.campanhas.view",
          },
          {
            name: "Chat Interno",
            href: "/dashboard/chat-interno",
            icon: Hash,
            permission: "chat-interno.view",
          },
          {
            name: "Mapa de Alertas",
            href: "/dashboard/suporte",
            icon: Map,
            permission: "suporte.alertas.view",
            module: "SUPORTE",
          },
          {
            name: "Massivas",
            href: "/dashboard/suporte/massivas",
            icon: Siren,
            permission: "suporte.massivas.view",
            module: "SUPORTE",
          },
        ],
      },
      {
        name: "Relatórios",
        icon: ChartColumn,
        items: [
          {
            name: "Painel Geral",
            href: "/dashboard/chat/relatorios",
            icon: BarChart3,
            permission: "chat.reports.view",
          },
          {
            name: "Monitoramento",
            href: "/dashboard/chat/supervisor",
            icon: Monitor,
            permission: "chat.supervisor.view",
          },
          {
            // #155: mesma permissão do Monitoramento (chat.supervisor.view) — a
            // ação de desconectar tem gate próprio, dentro da tela.
            name: "Atendentes Online",
            href: "/dashboard/chat/agentes",
            icon: Headset,
            permission: "chat.supervisor.view",
          },
          {
            name: "Relatório Conversas",
            href: "/dashboard/chat/relatorios/conversas",
            // Gate alinhado ao backend: os endpoints de dados desta tela
            // (/chat/reports/conversas + os *-list do filtro) exigem
            // `chat.reports.view`, e o recorte fino .equipe/.todos e feito
            // dentro do endpoint (resolveTeamScope). Gatear o menu por
            // chat.reports.conversas.view mostrava o item a cargos que tomavam
            // 403 nos dados (front<>back inconsistente).
            icon: FileBarChart,
            permission: "chat.reports.view",
          },
          {
            // "Origem" colidia: no Comercial, `canalOrigem` do lead ja significa
            // CANAL (WhatsApp, Instagram, site) — e este relatorio tem um card
            // "Por canal" dentro dele, entao o nome antigo prometia justamente
            // o pedaco errado. "Recebidos e Efetuados" sao as duas palavras que
            // o usuario ve nos KPIs ao abrir, e o mesmo par do painel de voz.
            name: "Recebidos e Efetuados",
            href: "/dashboard/chat/relatorios/origem",
            // ArrowLeftRight e o que o relatorio mede: direcao do contato —
            // quem foi na direcao de quem (Recebido <- / Efetuado ->).
            // FileBarChart ja aparece 3x no menu e nao distinguia nada.
            icon: ArrowLeftRight,
            permission: "chat.reports.view",
          },
          {
            name: "Cockpit Atendentes",
            href: "/dashboard/chat/relatorios/atendentes",
            icon: Gauge,
            permission: "chat.reports.atendentes.view",
          },
          {
            name: "CSAT",
            href: "/dashboard/chat/relatorios/csat",
            icon: Heart,
            permission: "chat.reports.view",
          },
          {
            // Pesquisa por fluxo das campanhas (#614): consolidado por fluxo. Ao lado do CSAT,
            // mas é outra régua — e a permissão é a das campanhas, a que a API pede.
            name: "Pesquisas",
            href: "/dashboard/chat/relatorios/pesquisas",
            icon: ClipboardList,
            permission: "chat.campanhas.view",
            badge: "novo",
          },
          {
            name: "Motivos",
            href: "/dashboard/chat/relatorios/motivos",
            icon: ListChecks,
            permission: "chat.reports.view",
          },
          {
            name: "Fila",
            href: "/dashboard/chat/relatorios/fila",
            icon: Funnel,
            permission: "chat.reports.view",
          },
          {
            name: "Devoluções",
            href: "/dashboard/chat/relatorios/devolucoes",
            // Repeat2 e o gesto que o relatorio mede: a conversa voltando pra
            // fila. Fica ao lado de "Fila" de proposito — sao os dois lados da
            // mesma operacao (entrar na fila / voltar pra ela).
            icon: Repeat2,
            permission: "chat.reports.view",
          },
          {
            name: "Pausas",
            href: "/dashboard/chat/relatorios/pausas",
            icon: PauseCircle,
            permission: "chat.reports.view",
          },
          {
            name: "Evolução",
            href: "/dashboard/chat/relatorios/evolucao",
            icon: TrendingUp,
            permission: "chat.reports.view",
          },
        ],
      },
      {
        name: "Configurações",
        icon: Settings,
        items: [
          {
            name: "Times",
            href: "/dashboard/chat/settings/times",
            icon: Users,
            permission: "chat.settings.teams.view",
          },
          {
            name: "Atendentes",
            href: "/dashboard/chat/settings/agentes",
            icon: UserCog,
            permission: "chat.settings.agents.view",
          },
          {
            name: "Automações",
            href: "/dashboard/chat/automacoes",
            icon: Zap,
            permission: "chat.automacoes.view",
          },
          {
            name: "Fluxos de Conversa",
            href: "/dashboard/chat/flows",
            icon: Workflow,
            permission: "chat.flows.editar",
          },
          {
            name: "Robôs",
            href: "/dashboard/chat/robos",
            icon: Bot,
            permission: "chat.robos.view",
          },
          {
            // GK Admin only — apenas a equipe GK Hub enxerga (cadeado na UI).
            name: "Agentes IA",
            href: "/dashboard/chat/agentes-ia",
            icon: Bot,
            gkAdminOnly: true,
          },
          {
            name: "Respostas Rápidas",
            href: "/dashboard/chat/settings/respostas",
            icon: MessageSquareReply,
            permission: "chat.settings.canned.view",
          },
          {
            name: "Etiquetas",
            href: "/dashboard/chat/settings/etiquetas",
            icon: Tags,
            permission: "chat.settings.labels.view",
          },
          {
            name: "Motivos",
            href: "/dashboard/chat/settings/motivos",
            icon: MessageCircle,
            permission: "chat.settings.motivos.view",
          },
          {
            name: "Motivos de pausa",
            href: "/dashboard/chat/settings/pausas",
            icon: PauseCircle,
            permission: "chat.settings.pausas.view",
          },
          {
            name: "Testes de conexão",
            href: "/dashboard/chat/settings/testes-conexao",
            icon: Wifi,
            anyOfPermissions: ["chat.settings.acs.view", "chat.settings.acs.manage"],
          },
          {
            name: "Feriados",
            href: "/dashboard/chat/settings/feriados",
            icon: CalendarDays,
            permission: "chat.settings.holidays.view",
          },
          {
            name: "Integrações",
            href: "/dashboard/chat/settings/integracoes",
            icon: Cable,
            permission: "chat.settings.integrations.view",
          },
          {
            name: "Canais",
            href: "/dashboard/chat/settings/canais",
            icon: Inbox,
            permission: "chat.settings.channels.view",
          },
          {
            name: "Suporte",
            href: "/dashboard/suporte/settings",
            icon: Sliders,
            permission: "suporte.configurar",
            module: "SUPORTE",
          },
        ],
      },
    ],
  },
  {
    name: "Cobrança",
    href: "/dashboard/cobranca",
    icon: Receipt,
    module: "COBRANCA",
    permission: "cobranca.view",
    children: [
      {
        name: "Régua de Cobrança",
        href: "/dashboard/cobranca/reguas",
        icon: ListChecks,
        permission: "cobranca.reguas.view",
      },
      {
        name: "Monitor",
        href: "/dashboard/cobranca/monitor",
        icon: Activity,
        permission: "cobranca.monitor.view",
      },
      {
        name: "Carteiras",
        href: "/dashboard/cobranca/carteiras",
        icon: Wallet,
        permission: "cobranca.view",
      },
      {
        name: "Exceções",
        href: "/dashboard/cobranca/excecoes",
        icon: ShieldOff,
        permission: "cobranca.view",
      },
      // Entidade homônima da de cima, com outro significado: aquela é segmento
      // de filtros das réguas automáticas, esta é a divisão da base inadimplente
      // entre cobradores. Os rótulos precisam desambiguar — ver §3 da spec
      // 2026-07-29-carteira-cobranca-colaborador-design.md.
      {
        name: "Carteira por colaborador",
        href: "/dashboard/cobranca/carteiras-colaborador",
        icon: HandCoins,
        permission: "cobranca.carteiras.view",
        module: "CARTEIRA_COBRANCA",
      },
      {
        // Feriados da COBRANÇA (tabela `feriados` da Platform, lida pela régua).
        // NÃO confundir com Chat > Feriados, que é outra feature, em outro banco
        // (account_holiday, no motor de chat). Esta tela ficava órfã em
        // /dashboard/settings/feriados — fora do menu, inalcançável pelo ISP.
        name: "Feriados",
        href: "/dashboard/cobranca/feriados",
        icon: CalendarDays,
        permission: "cobranca.view",
      },
      {
        name: "Inadimplência",
        href: "/dashboard/cobranca/inadimplencia",
        icon: AlertTriangle,
        permission: "cobranca.inadimplencia.view",
      },
      {
        name: "Relatórios",
        href: "/dashboard/cobranca/relatorios",
        icon: BarChart3,
        permission: "cobranca.relatorios.view",
      },
    ],
  },
  {
    name: "Comercial",
    href: "/dashboard/comercial",
    icon: Kanban,
    module: "COMERCIAL",
    permission: "comercial.view",
    children: [
      {
        name: "Dashboard",
        href: "/dashboard/comercial",
        icon: LayoutDashboard,
        permission: "comercial.view",
      },
      {
        name: "Leads",
        href: "/dashboard/comercial/leads",
        icon: Users,
        permission: "comercial.leads.view",
      },
      // "Funil" (a visualização de conversão, /dashboard/comercial/funil)
      // ocultado do menu a pedido do Gabriel em 11/08: dois itens quase
      // homônimos lado a lado confundiam mais do que ajudavam. A PÁGINA
      // continua no ar e acessível por URL — só saiu da navegação, e voltar é
      // descomentar. Mesmo padrão das Metas, logo abaixo.
      // {
      //   name: "Funil",
      //   href: "/dashboard/comercial/funil",
      //   icon: Funnel,
      //   permission: "comercial.view",
      // },
      {
        // Construtor de funil: é aqui que o funil ganha time, e é o vínculo com
        // o time que declara aquele time como comercial.
        name: "Funis",
        href: "/dashboard/comercial/funis",
        icon: GitBranch,
        permission: "comercial.funil.configurar",
      },
      {
        name: "Kanban",
        href: "/dashboard/comercial/kanban",
        icon: Columns3,
        // Kanban é uma visão de LEADS (o back aplica escopo own/team) — o code
        // antigo "comercial.kanban.view" não existe na tabela permissions, então
        // cargo personalizado nunca via o item (auditoria RBAC Lote 2).
        permission: "comercial.leads.view",
      },
      {
        name: "Inteligência",
        href: "/dashboard/comercial/inteligencia",
        icon: TrendingUp,
        permission: "comercial.view",
      },
      {
        name: "Performance",
        href: "/dashboard/comercial/performance",
        icon: Trophy,
        permission: "comercial.performance.view",
      },
      // v1.0 Comercial: Metas ocultas (não usadas nesta versão; reversível).
      // {
      //   name: "Metas",
      //   href: "/dashboard/comercial/metas-v2",
      //   icon: Target,
      //   permission: "comercial.metas.view",
      // },
      {
        name: "Relatórios",
        href: "/dashboard/comercial/relatorios",
        icon: FileBarChart,
        permission: "comercial.view",
      },
      {
        name: "Configurações",
        href: "/dashboard/comercial/settings",
        icon: Sliders,
        permission: "comercial.configurar",
      },
    ],
  },
  {
    name: "CX",
    href: "/dashboard/cx",
    icon: HeartHandshake,
    module: "CX",
    permission: "cx.recorrencia.view",
    badge: "novo",
    children: [
      {
        name: "Clientes",
        href: "/dashboard/clientes",
        icon: Users,
        permission: "clientes.ver",
      },
      {
        name: "Recorrência",
        href: "/dashboard/cx/recorrencia",
        icon: Route,
        permission: "cx.recorrencia.view",
        badge: "beta",
      },
      {
        name: "Churn Predictor",
        href: "/dashboard/cx/churn",
        icon: TrendingDown,
        permission: "cx.churn.view",
        badge: "novo",
      },
      {
        name: "Contatos excluídos",
        href: "/dashboard/cx/recorrencia/excluidos",
        icon: UserX,
        permission: "cx.recorrencia.manage_exclusions",
      },
      {
        name: "Qualidade do atendimento",
        href: "#",
        icon: Star,
        disabled: true,
        badge: "em breve",
      },
    ],
    // A jornada agrupa o que acompanha o cliente ao longo do tempo. Hoje só o
    // berçário (primeiros dias depois da instalação); é onde entram as próximas
    // fases da vida do cliente.
    groups: [
      {
        name: "Jornada do cliente",
        icon: Map,
        items: [
          {
            name: "Berçário",
            href: "/dashboard/cx/bercario",
            icon: Heart,
            permission: "cx.bercario.view",
            badge: "beta",
          },
        ],
      },
    ],
  },
  {
    // Telefonia (voice) é SKU de RUNTIME, não módulo de licenciamento do
    // tenant — gated por getVoiceEnabled() (vem do backend via /auth/permissions)
    // no filtro da nav (não usa `module`, que passa pelo hasModule/TenantModule).
    name: "Telefonia",
    href: "/dashboard/telefonia",
    icon: Phone,
    permission: "telefonia.dashboard.ver",
    children: [
      {
        name: "Visão ao vivo",
        href: "/dashboard/telefonia",
        icon: Activity,
      },
      {
        name: "Supervisor",
        href: "/dashboard/telefonia/supervisor",
        icon: Users,
        permission: "telefonia.supervisor",
      },
      {
        name: "Histórico",
        href: "/dashboard/telefonia/historico",
        icon: Phone,
        permission: "telefonia.historico.ver",
      },
      {
        name: "Relatórios",
        href: "/dashboard/telefonia/relatorios",
        icon: BarChart3,
        permission: "telefonia.historico.ver",
      },
    ],
  },
  // Ocultados a pedido (reversível) — descomentar para reexibir na sidebar.
  // {
  //   name: "GK.IA",
  //   href: "/dashboard/gk-ia",
  //   icon: Bot,
  //   permission: "ia.view",
  //   children: [
  //     { name: "Relatórios", href: "/dashboard/gk-ia", icon: Brain, permission: "ia.view" },
  //     { name: "Configurações", href: "/dashboard/gk-ia/settings", icon: Sliders, permission: "ia.configurar" },
  //   ],
  // },
  // {
  //   name: "Fluxos",
  //   href: "/dashboard/automacoes",
  //   icon: Workflow,
  //   permission: "chat.automacoes.view",
  //   children: [],
  // },
];

// #466: onde o Chat Interno mora no menu — achado no array, não fixo, pra o
// contador de não-lidas acompanhar se o item mudar de grupo ou de módulo.
const INTERNAL_CHAT_HREF = "/dashboard/chat-interno";
const internalChatLocation = (() => {
  for (const m of modules) {
    for (const g of m.groups ?? []) {
      const item = g.items.find((i) => i.href === INTERNAL_CHAT_HREF);
      if (item) return { moduleHref: m.href, groupKey: `${m.href}::${g.name}`, item };
    }
    const item = m.children?.find((i) => i.href === INTERNAL_CHAT_HREF);
    if (item) return { moduleHref: m.href, groupKey: null, item };
  }
  return null;
})();

const bottomItems: Array<{ name: string; href: string; icon: React.ComponentType<{ className?: string }>; adminOnly?: boolean; permission?: string; anyPermission?: string }> = [
  {
    name: "GK Admin",
    href: "/admin",
    icon: Shield,
    adminOnly: true,
  },
  {
    name: "Empresa",
    href: "/dashboard/settings/empresa",
    icon: Building2,
    permission: "admin.empresa.view",
  },
  {
    name: "Configurações",
    href: "/dashboard/settings",
    icon: Settings,
    anyPermission: "settings.",
  },
  {
    name: "Auditoria",
    href: "/dashboard/settings/auditoria",
    icon: FileText,
    permission: "settings.auditoria.view",
  },
];

interface SidebarProps {
  onToggleTheme?: () => void;
  isDark?: boolean;
  user?: AuthUser | null;
}

export function Sidebar({ onToggleTheme, isDark = true, user }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [collapsed, setCollapsed] = useState(false);
  useLivePermissions();
  const { total: internalUnread } = useInternalUnread();
  const internalTier = unreadTier(internalUnread);

  // Expoe a largura da sidebar como CSS var no <html> pra Dialogs (Portal
  // renderiza fora do <aside> entao precisa de var global) centralizarem
  // no <main> em vez do viewport inteiro. Limpa no unmount pra paginas
  // sem sidebar (ex: /login) caírem no fallback 0.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--app-sidebar-width", collapsed ? "4rem" : "16rem");
    return () => {
      root.style.removeProperty("--app-sidebar-width");
    };
  }, [collapsed]);

  // Ao ENTRAR na caixa de conversas do chat, minimiza a sidebar sozinha (3
  // colunas + Cockpit apertam a tela). Ao SAIR, reexpande. Age só na troca de
  // "zona" — então expandir manualmente enquanto navega entre conversas é
  // respeitado (não força recolher a cada render).
  const isChatInbox =
    pathname === "/dashboard/chat" || /^\/dashboard\/chat\/\d+/.test(pathname);
  const prevChatZoneRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevChatZoneRef.current === isChatInbox) return;
    prevChatZoneRef.current = isChatInbox;
    setCollapsed(isChatInbox);
  }, [isChatInbox]);

  // Ler avatarUrl direto do localStorage (pode ser atualizado após login)
  const avatarSrc = (() => {
    const u = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user") || "null") : null;
    return u?.avatarUrl ? `${process.env.NEXT_PUBLIC_API_URL || "/api"}${u.avatarUrl}` : null;
  })();
  const fotoDoUsuario = useFotoQueFalha(avatarSrc); // #579: foto que falha cai nas iniciais
  const [expandedModules, setExpandedModules] = useState<string[]>(() => {
    const active = modules.find(
      (m) =>
        (m.children?.length || m.groups?.length) &&
        (pathname.startsWith(m.href) ||
          (m.groups?.some((g) =>
            g.items.some((i) => pathname === i.href || pathname.startsWith(i.href + "/"))
          ) ?? false))
    );
    return active ? [active.href] : [];
  });

  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    // Todos os módulos com grupos, não só o primeiro. Antes só o Chat tinha
    // grupo e a busca parava nele — com o CX ganhando "Jornada do cliente",
    // entrar direto no berçário deixaria o grupo fechado e o item escondido.
    const keys = modules
      .filter((m) => m.groups && m.groups.length > 0)
      .map((m) =>
        findActiveGroupKey(
          pathname,
          m.groups!.map((g) => ({
            key: `${m.href}::${g.name}`,
            hrefs: g.items.map((i) => i.href),
          })),
        ),
      )
      .filter((k): k is string => k !== null);
    return keys;
  });

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // #608: regra única — "Marina - IA" → "MI", não "M-".
  const initials = user?.name ? iniciais(user.name) : "GK";

  const toggleModule = (href: string) => {
    setExpandedModules((prev) =>
      prev.includes(href)
        ? prev.filter((h) => h !== href)
        : [...prev, href]
    );
  };

  const isSubItemVisible = (child: SubItem) => {
    if (child.module && !hasModule(child.module)) return false;
    if (child.gkAdminOnly && !getIsGkAdmin()) return false;
    if (child.permission && !hasPermission(child.permission)) return false;
    if (child.anyOfPermissions && !child.anyOfPermissions.some((c) => hasPermission(c))) return false;
    if (child.anyPermission && !getIsGkAdmin() && !hasAnyPermission(child.anyPermission)) return false;
    // Agenda e SKU de RUNTIME (AGENDA_ENABLED no backend), mesmo arranjo de
    // Telefonia (getVoiceEnabled() logo abaixo, no filtro de topo) — feature
    // publicada pro cliente antes de estar pronta, some da sidebar enquanto
    // nao amadurece. SEM bypass de GK Admin: e config de deploy, nao
    // permissao de cargo.
    if (child.href === "/dashboard/agenda" && !getAgendaEnabled()) return false;
    return true;
  };

  // #466: o contador aparece uma vez, no nível mais fundo que está à vista.
  const internalAnchor = internalChatLocation
    ? unreadAnchor({
        total: internalUnread,
        itemVisible: isSubItemVisible(internalChatLocation.item),
        sidebarCollapsed: collapsed,
        moduleExpanded: expandedModules.includes(internalChatLocation.moduleHref),
        groupExpanded:
          internalChatLocation.groupKey === null ||
          expandedGroups.includes(internalChatLocation.groupKey),
      })
    : null;
  const internalLabel = internalUnread > 99 ? "99+" : String(internalUnread);
  const internalPill = (
    <span
      aria-label={`${internalUnread} não lidas no Chat Interno`}
      className={cn(
        "inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none",
        internalTier === "high"
          ? "bg-red-500 text-white"
          : internalTier === "mid"
            ? "bg-amber-500/20 text-amber-300 ring-1 ring-inset ring-amber-500/40"
            : "bg-[#5DADE2]/15 text-[#5DADE2] ring-1 ring-inset ring-[#5DADE2]/30",
      )}
    >
      {internalLabel}
    </span>
  );

  const renderSubItem = (child: SubItem) => {
    const isChildActive = pathname === child.href;
    if (child.disabled) {
      return (
        <div
          key={child.href + child.name}
          className="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] font-medium text-sidebar-foreground/30 cursor-default"
        >
          <child.icon className="h-4 w-4 shrink-0" />
          <span className="flex-1">{child.name}</span>
          {child.badge && (
            <span className="rounded-full bg-sidebar-accent/50 px-1.5 py-0.5 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-wide leading-none">
              {child.badge}
            </span>
          )}
        </div>
      );
    }
    return (
      <Link
        key={child.href}
        href={child.href}
        className={cn(
          "flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors",
          isChildActive
            ? "bg-sidebar-accent/70 text-sidebar-accent-foreground"
            : "text-sidebar-foreground/60 hover:bg-sidebar-accent/30 hover:text-sidebar-foreground"
        )}
      >
        <child.icon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{child.name}</span>
        {child.href === INTERNAL_CHAT_HREF && internalAnchor === "item" && internalPill}
        {child.gkAdminOnly && (
          <Lock className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40" />
        )}
        {child.badge && (
          <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary uppercase tracking-wide leading-none">
            {child.badge}
          </span>
        )}
      </Link>
    );
  };

  async function handleLogout() {
    // Confirmacao sonora ANTES do await: depois da navegacao o audio e cortado.
    // O modulo de sons retem a referencia do elemento ate o `ended`, senao o GC
    // o coleta durante a navegacao e o som nunca sai.
    playNotificationSoundIfEnabled("saida");
    await logout();
    await withSpacesTransition(() => router.push("/login"), "backward");
  }

  return (
    <TooltipProvider delay={300}>
    <aside
      className={cn(
        "flex h-screen flex-col overflow-hidden bg-sidebar transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo + toggle */}
      <div
        className={cn(
          "shrink-0 px-3 pt-3 pb-2 flex gap-2",
          collapsed ? "flex-col items-center" : "flex-row items-center justify-between"
        )}
      >
        {collapsed ? (
          <img
            src="/logo-collapsed.svg"
            alt="GK"
            className="h-10 w-10 shrink-0 object-contain"
          />
        ) : (
          <img
            src="/logo-gk.svg"
            alt="GK Platform"
            className="h-14 w-auto flex-1 min-w-0 object-contain object-left"
          />
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expandir sidebar" : "Recolher sidebar"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
        {modules
          // Voice e SKU opcional de runtime — sem voz habilitada some da sidebar
          .filter((item) => getVoiceEnabled() || item.href !== "/dashboard/telefonia")
          .filter((item) =>
            isModuleItemVisible(item, { hasPermission, hasModule, isSubItemVisible })
          )
          .map((item) => {
          const isModuleActive =
            item.href === "/dashboard"
              ? pathname === "/dashboard"
              : pathname === item.href ||
                pathname.startsWith(item.href + "/") ||
                (item.groups?.some((g) =>
                  g.items.some((i) => pathname === i.href || pathname.startsWith(i.href + "/"))
                ) ?? false);
          const hasChildren = item.children && item.children.length > 0;
          const hasGroups = !!(item.groups && item.groups.length > 0);
          const isExpanded = expandedModules.includes(item.href);

          if (item.comingSoon) {
            const content = (
              <div
                key={item.name}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground/30 cursor-default"
              >
                <item.icon className="h-5 w-5 shrink-0" />
                <span
                  className={cn(
                    "whitespace-nowrap transition-opacity duration-200",
                    collapsed && "opacity-0 pointer-events-none"
                  )}
                >
                  {item.name}
                </span>
                <span
                  className={cn(
                    "ml-auto rounded-full bg-sidebar-accent/50 px-2 py-0.5 text-[10px] font-semibold text-sidebar-foreground/40 whitespace-nowrap transition-opacity duration-200",
                    collapsed && "opacity-0 pointer-events-none"
                  )}
                >
                  Em breve
                </span>
              </div>
            );
            if (collapsed) {
              return (
                <Tooltip key={item.name}>
                  <TooltipTrigger render={content} />
                  <TooltipContent side="right">{item.name} · Em breve</TooltipContent>
                </Tooltip>
              );
            }
            return content;
          }

          // #466: recolhida, só o ícone do módulo fica à vista — o contador
          // vira um número no canto dele, em cor sólida (contorno some em 20px).
          const internalOnIcon =
            collapsed &&
            internalAnchor === "module" &&
            item.href === internalChatLocation?.moduleHref;

          if ((!hasChildren && !hasGroups) || collapsed) {
            const link = (
              <Link
                href={item.href}
                className={cn(
                  "relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isModuleActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {internalOnIcon && (
                  <span
                    aria-label={`${internalUnread} não lidas no Chat Interno`}
                    className={cn(
                      "absolute left-6 top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none ring-2 ring-sidebar",
                      internalTier === "high"
                        ? "bg-red-500 text-white"
                        : internalTier === "mid"
                          ? "bg-amber-500 text-sidebar"
                          : "bg-[#5DADE2] text-sidebar",
                    )}
                  >
                    {internalLabel}
                  </span>
                )}
                <span
                  className={cn(
                    "whitespace-nowrap transition-opacity duration-200",
                    collapsed && "opacity-0 pointer-events-none"
                  )}
                >
                  {item.name}
                </span>
              </Link>
            );
            if (collapsed) {
              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger render={link} />
                  <TooltipContent side="right">
                    {internalOnIcon
                      ? `${item.name} · ${internalLabel} não lidas no Chat Interno`
                      : item.name}
                  </TooltipContent>
                </Tooltip>
              );
            }
            return <div key={item.href}>{link}</div>;
          }

          return (
            <div key={item.href} className="space-y-0.5">
              <button
                onClick={() => toggleModule(item.href)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isModuleActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                <span className="flex-1 text-left whitespace-nowrap">{item.name}</span>
                {internalAnchor === "module" &&
                  item.href === internalChatLocation?.moduleHref &&
                  internalPill}
                {item.badge && (
                  <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary uppercase tracking-wide leading-none">
                    {item.badge}
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 transition-transform duration-200",
                    isExpanded && "rotate-180"
                  )}
                />
              </button>

              <div
                className={cn(
                  "transition-all duration-200",
                  isExpanded
                    ? "max-h-[1200px] opacity-100"
                    : "max-h-0 opacity-0 overflow-hidden"
                )}
              >
                {/* Itens soltos e grupos juntos: o CX tem os dois. Antes era um
                    OU outro, e os soltos sumiriam ao entrar o primeiro grupo. */}
                {hasChildren && (
                  <div className="ml-4 space-y-0.5 border-l border-sidebar-border/50 pl-2">
                    {item.children!.filter(isSubItemVisible).map(renderSubItem)}
                  </div>
                )}
                {hasGroups && (
                  <div className="ml-4 space-y-1 border-l border-sidebar-border/50 pl-2">
                    {item.groups!.map((group) => {
                      const visibleItems = group.items.filter(isSubItemVisible);
                      if (visibleItems.length === 0) return null;
                      const groupKey = `${item.href}::${group.name}`;
                      const groupExpanded = expandedGroups.includes(groupKey);
                      return (
                        <div key={groupKey} className="space-y-0.5">
                          <button
                            type="button"
                            onClick={() => toggleGroup(groupKey)}
                            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
                          >
                            <group.icon className="h-4 w-4 shrink-0" />
                            <span className="flex-1 text-left whitespace-nowrap">{group.name}</span>
                            {internalAnchor === "group" &&
                              groupKey === internalChatLocation?.groupKey &&
                              internalPill}
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
                                groupExpanded && "rotate-180"
                              )}
                            />
                          </button>
                          <div
                            className={cn(
                              "transition-all duration-200",
                              groupExpanded
                                ? "max-h-[800px] opacity-100"
                                : "max-h-0 opacity-0 overflow-hidden"
                            )}
                          >
                            <div className="ml-3 space-y-0.5 border-l border-sidebar-border/40 pl-2">
                              {visibleItems.map(renderSubItem)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-sidebar-border px-2 py-2 space-y-1 shrink-0">
        {/* Nav items */}
        {bottomItems.filter((item) => {
          if (item.adminOnly && !getIsGkAdmin()) return false;
          if ("permission" in item && item.permission && !hasPermission(item.permission)) return false;
          if ("anyPermission" in item && item.anyPermission && !getIsGkAdmin() && !hasAnyPermission(item.anyPermission)) return false;
          return true;
        }).map((item) => {
          const isActive =
            pathname === item.href || pathname.startsWith(item.href + "/");
          const link = (
            <Link
              href={item.href}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span
                className={cn(
                  "whitespace-nowrap transition-opacity duration-200",
                  collapsed && "opacity-0 pointer-events-none"
                )}
              >
                {item.name}
              </span>
            </Link>
          );
          if (collapsed) {
            return (
              <Tooltip key={item.href}>
                <TooltipTrigger render={link} />
                <TooltipContent side="right">{item.name}</TooltipContent>
              </Tooltip>
            );
          }
          return <div key={item.href}>{link}</div>;
        })}

        {/* Actions row: theme + notifications */}
        <div className={cn(
          "flex items-center gap-1 pt-1 px-1",
          collapsed ? "flex-col" : ""
        )}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
            onClick={onToggleTheme}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <NotificationBell />
        </div>

        {/* User profile */}
        <div className="pt-1 border-t border-sidebar-border/50">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <button
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-sidebar-accent/50",
                    collapsed && "justify-center px-0"
                  )}
                >
                  <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full">
                    {avatarSrc && fotoDoUsuario.mostraFoto ? (
                      <img
                        src={avatarSrc}
                        alt={user?.name || ""}
                        className="h-full w-full object-cover"
                        onError={fotoDoUsuario.aoFalhar}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-primary text-primary-foreground text-xs">
                        {initials}
                      </div>
                    )}
                  </div>
                  <div
                    className={cn(
                      "flex-1 text-left min-w-0 transition-opacity duration-200",
                      collapsed && "opacity-0 pointer-events-none w-0"
                    )}
                  >
                    <p className="text-sm font-medium text-sidebar-foreground truncate whitespace-nowrap">
                      {user?.name || "Usuário"}
                    </p>
                    <p className="text-[11px] text-sidebar-foreground/50 truncate whitespace-nowrap">
                      {user?.tenantName || ""}
                    </p>
                  </div>
                </button>
              }
            />
            <DropdownMenuContent side="top" align="start" className="w-56">
              <SidebarAgentStatus />
              <DropdownMenuItem onClick={() => router.push("/dashboard/perfil")}>Meu Perfil</DropdownMenuItem>
              {(getIsGkAdmin() || hasAnyPermission("settings.")) && (
                <DropdownMenuItem onClick={() => router.push("/dashboard/settings")}>Configurações</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </aside>
    </TooltipProvider>
  );
}
